import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { useOptionalFeature } from "@/lib/features";

import type { ReaderFilters } from "@/lib/report-builder/reader-filters";
import type {
  CreateCustomReportRequest,
  CustomReport,
  CustomReportSummary,
  UpdateCustomReportRequest,
} from "@/types/reports";

/**
 * The network layer for custom reports (`/api/reports/custom`) and the
 * tenant's standard reports (`/api/reports/standard`).
 *
 * The lists and each report are separate cache entries: the lists carry no
 * layouts (the server computes their page and block counts), so writing one
 * report updates that report's entry in place and only marks the lists stale.
 * A standard report is read through the same `custom/:id` entry as any.
 */
export const CUSTOM_REPORTS_KEY = ["reports", "custom"] as const;
export const STANDARD_REPORTS_KEY = ["reports", "standard"] as const;
export const customReportKey = (id: string) => [...CUSTOM_REPORTS_KEY, id] as const;

/** The caller's reports, most recently edited first. Off without the feature. */
export function useCustomReports(options: { enabled?: boolean } = {}) {
  const featureOn = useOptionalFeature("report_builder");
  return useQuery({
    queryKey: CUSTOM_REPORTS_KEY,
    queryFn: () => api.get<CustomReportSummary[]>("/api/reports/custom"),
    enabled: featureOn && (options.enabled ?? true),
  });
}

/**
 * The tenant's standard reports the caller can see all of, in the server's
 * order. Off without the feature.
 */
export function useStandardReports(options: { enabled?: boolean } = {}) {
  const featureOn = useOptionalFeature("report_builder");
  return useQuery({
    queryKey: STANDARD_REPORTS_KEY,
    queryFn: () => api.get<CustomReportSummary[]>("/api/reports/standard"),
    enabled: featureOn && (options.enabled ?? true),
    // Code-defined: they change with a deploy, not while the page is open.
    staleTime: 5 * 60_000,
  });
}

export function useCustomReport(id: string | undefined) {
  return useQuery({
    queryKey: customReportKey(id ?? "none"),
    queryFn: () => api.get<CustomReport>(`/api/reports/custom/${id}`),
    enabled: !!id,
  });
}

export function useCreateCustomReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCustomReportRequest) =>
      api.post<CustomReport>("/api/reports/custom", body),
    onSuccess: (report) => {
      qc.setQueryData(customReportKey(report.id), report);
      void qc.invalidateQueries({ queryKey: CUSTOM_REPORTS_KEY, exact: true });
    },
  });
}

export function useUpdateCustomReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateCustomReportRequest }) =>
      api.patch<CustomReport>(`/api/reports/custom/${id}`, body),
    onSuccess: (report) => {
      qc.setQueryData(customReportKey(report.id), report);
      void qc.invalidateQueries({ queryKey: CUSTOM_REPORTS_KEY, exact: true });
    },
  });
}

/**
 * Saves the caller's own filters on a standard report (the whole set; `{}`
 * resets) and caches the report as it comes back, with them on.
 */
export function useSetReportFilters(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (filters: ReaderFilters) =>
      api.put<CustomReport>(`/api/reports/custom/${id}/filters`, { filters }),
    onSuccess: (report) => {
      qc.setQueryData(customReportKey(report.id), report);
    },
  });
}

export function useDuplicateCustomReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<CustomReport>(`/api/reports/custom/${id}/duplicate`, {}),
    onSuccess: (report) => {
      qc.setQueryData(customReportKey(report.id), report);
      void qc.invalidateQueries({ queryKey: CUSTOM_REPORTS_KEY, exact: true });
    },
  });
}

export function useDeleteCustomReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/api/reports/custom/${id}`),
    onSuccess: (_void, id) => {
      qc.removeQueries({ queryKey: customReportKey(id), exact: true });
      void qc.invalidateQueries({ queryKey: CUSTOM_REPORTS_KEY, exact: true });
    },
  });
}
